import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PlanSelectionModal from '../components/chat/sidebar/PlanSelectionModal';

interface PlanModalContextType {
  openPlanModal: (currentPriceId?: string) => void;
  closePlanModal: () => void;
  isPlanModalOpen: boolean;
}

const PlanModalContext = createContext<PlanModalContextType | null>(null);

interface PlanModalProviderProps {
  children: React.ReactNode;
}

export const PlanModalProvider: React.FC<PlanModalProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentPriceIdRef = useRef<string | undefined>(undefined);

  const openPlanModal = useCallback((currentPriceId?: string) => {
    currentPriceIdRef.current = currentPriceId;
    setIsOpen(true);
  }, []);

  const closePlanModal = useCallback(() => {
    setIsOpen(false);
    currentPriceIdRef.current = undefined;
  }, []);

  const contextValue = useMemo<PlanModalContextType>(
    () => ({ openPlanModal, closePlanModal, isPlanModalOpen: isOpen }),
    [openPlanModal, closePlanModal, isOpen],
  );

  return (
    <PlanModalContext.Provider value={contextValue}>
      {children}
      <PlanSelectionModal
        isOpen={isOpen}
        onClose={closePlanModal}
        currentPriceId={currentPriceIdRef.current}
      />
    </PlanModalContext.Provider>
  );
};

export const usePlanModal = (): PlanModalContextType => {
  const context = useContext(PlanModalContext);
  if (!context) {
    throw new Error('usePlanModal must be used within PlanModalProvider');
  }
  return context;
};
